import React, { useState } from 'react';
import moment from 'moment';
import 'moment-timezone';
import Select from 'react-select';
import { Tooltip, OverlayTrigger, ProgressBar } from 'react-bootstrap';
import styles from './Planograms.module.css';
import { SchedulePlanogramUpdate } from '../libs/GraphQL';
import ValidationFeedback from '../components/ValidationFeedback/ValidationFeedback';

const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const getCurrentTimeForActiveHour = () => {
  const now = moment.tz('America/Chicago');
  const activeHour = now.format('MM/DD/YYYY HH:mm:ss');
  return activeHour;
};

const chunkNewPlanograms = (array) => {
  const layoutMap = new Map();
  array.forEach((input) => {
    const { layoutName } = input;
    if (!layoutMap.has(layoutName)) {
      layoutMap.set(layoutName, []);
    }
    layoutMap.get(layoutName).push(input);
  });

  // Return array of arrays, one per layoutName
  return Array.from(layoutMap.values());
};

const PlanogramTaskOptions = [
  { value: '', label: '-- Select One --' },
  { value: 'UPDATE', label: 'Update Existing Planogram' },
  { value: 'CREATE', label: 'Create New Planogram' },
  { value: 'RECONFIG', label: 'Reconfigure Existing Planogram' },
];

const VALIDATION_TYPE = {
  ERRORS: 'errors',
  WARNINGS: 'warnings',
  SUCCESS: 'success',
};

const isEmpty = (value) => {
  return value.trim() === '';
};

const pad = (strNumber, length = 2) => strNumber.padStart(length, '0');

const tooltipUpdate = (
  <Tooltip id="tooltip">
    <p className={styles.tooltipText}>
      activeHour must be in America/Chicago timezone.
    </p>
  </Tooltip>
);

const tooltipCreateReconfig = (
  <Tooltip id="tooltip-create-reconfig">
    <p className={styles.tooltipText}>
      Planogram creation and reconfiguration go into effect immediately if
      request is valid.
    </p>
  </Tooltip>
);

const Planograms = () => {
  const [selectedOption, setSelectedOption] = useState(PlanogramTaskOptions[0]);
  const [csvFile, setCsvFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const isValidSlot = (slot) => {
    const pattern = /^\d+:\d+$/;
    return pattern.test(slot);
  };

  const getDatetimeFormatted = (activeHour) => {
    try {
      const [date, time] = activeHour.split(' ');
      if (!date || !time) return null;
      const [hours, minutes, seconds] = time.split(':');
      const [month, day, year] = date.split('/');
      return `${pad(month)}/${pad(day)}/${year} ${pad(hours)}:${pad(
        minutes,
      )}:${pad(seconds)}`;
    } catch {
      return null;
    }
  };

  const validateActiveHour = (activeHour) => {
    const datetime = moment.tz(
      getDatetimeFormatted(activeHour),
      'MM/DD/YYYY HH:mm:ss',
      true,
      'America/Chicago',
    );
    const isUpdate = PlanogramTaskOptions.value === 'UPDATE';
    if (!datetime.isValid()) {
      return {
        isValid: false,
        errorMessage: 'The activeHour is not in format MM/DD/YYYY hh:mm:ss.',
      };
    }
    if (isUpdate && !datetime.isAfter(moment.tz('America/Chicago'))) {
      return {
        isValid: false,
        errorMessage: 'The activeHour must be in the future.',
      };
    }
    if (isUpdate && (datetime.minute() !== 0 || datetime.second() !== 0)) {
      return {
        isValid: false,
        errorMessage: 'The activeHour must be at an exact hour (HH:00:00).',
      };
    }
    return {
      isValid: true,
      errorMessage: '',
    };
  };

  const getDataFromCsv = (csvText) => {
    const rows = csvText
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line);
    const headers = rows.shift().split(',');
    return { headers, rows };
  };

  const validateCsvContent = (csvText, optionValueOverride) => {
    setFeedback(null);
    setErrors([]);

    const optionValue = optionValueOverride || selectedOption.value;

    const csvErrors = [];
    const warnings = [];
    const { headers, rows } = getDataFromCsv(csvText);
    const mapLayoutSlot = new Map();
    const mapLayoutSlotHour = new Map();

    // Header-level validation for mutually exclusive columns
    const normalizedHeaders = headers.map((h) => h.trim());
    if (optionValue === 'CREATE' || optionValue === 'RECONFIG') {
      if (normalizedHeaders.includes('activeHour')) {
        csvErrors.push({
          title: 'Invalid CSV',
          description:
            'The "activeHour" column is not allowed. Planogram creation and reconfiguration occur immediately if request is valid.',
        });
      }
    } else if (optionValue === 'UPDATE') {
      if (normalizedHeaders.includes('physicalFridgeConfig')) {
        csvErrors.push({
          title: 'Invalid CSV',
          description:
            'The "physicalFridgeConfig" header is not allowed for UPDATE uploads. Remove this column from your CSV or use "Reconfigure" or "Create" option.',
        });
      }
    }

    if (!rows) {
      csvErrors.push({
        title: `Line 1`,
        description:
          'No rows found. Provide at least one row with layoutName, slot, vendItemName, and activeHour.',
      });
      return;
    }

    rows.forEach((row, rowIndex) => {
      let layoutName, physicalFridgeConfig, slot, vendItemName, activeHour;
      const columns = row.split(',');
      if (optionValue === 'CREATE' || optionValue === 'RECONFIG') {
        [layoutName, slot, vendItemName, physicalFridgeConfig] = columns;
        activeHour = getCurrentTimeForActiveHour();
      } else {
        [layoutName, slot, vendItemName, activeHour] = columns;
      }
      const keyLayoutSlotHour = `${layoutName}${slot}${activeHour}`;
      const isLayoutSlotHourDuplicate =
        mapLayoutSlotHour.has(keyLayoutSlotHour);
      const keyLayoutSlot = `${layoutName}${slot}`;
      const isLayoutSlotDuplicate = mapLayoutSlot.has(keyLayoutSlot);

      if (isLayoutSlotHourDuplicate) {
        const duplicateIndex = mapLayoutSlotHour.get(keyLayoutSlotHour);
        csvErrors.push({
          title: `Duplicated records: Lines ${duplicateIndex + 2} and ${
            rowIndex + 2
          }`,
          description:
            'layoutName, slot, and activeHour are the same, but vendItem is different.',
        });
      } else if (isLayoutSlotDuplicate) {
        const duplicateIndex = mapLayoutSlot.get(keyLayoutSlot);
        warnings.push({
          title: `Duplicated records: lines ${duplicateIndex + 2} and ${
            rowIndex + 2
          }`,
          description: 'layoutName and slot are identical.',
        });
      }
      if (!isLayoutSlotHourDuplicate)
        mapLayoutSlotHour.set(keyLayoutSlotHour, rowIndex);
      if (!isLayoutSlotDuplicate) mapLayoutSlot.set(keyLayoutSlot, rowIndex);

      if (columns.length < 4) {
        csvErrors.push({
          title: `Line ${rowIndex + 2}`,
          description:
            'At least one input missing. Expected inputs include layoutName, slot, vendItemName, and activeHour.',
        });
      }
      columns.forEach((col, colIndex) => {
        if (isEmpty(col)) {
          csvErrors.push({
            title: `Line ${rowIndex + 2}, ${headers[colIndex]}`,
            description: 'Value cannot be empty.',
          });
        }
      });
      if (!isValidSlot(slot)) {
        csvErrors.push({
          title: `Line ${rowIndex + 2}, ${headers[1]}: '${slot}'`,
          description: 'Slot is not in format <integer>:<integer>.',
        });
      }
      const { isValid, errorMessage } = validateActiveHour(activeHour);
      if (!isValid) {
        csvErrors.push({
          title: `Line ${rowIndex + 2}, ${headers[3]}: '${activeHour}'`,
          description: errorMessage,
        });
      }
    });

    setErrors(csvErrors);
    if (warnings.length) {
      setFeedback({ type: VALIDATION_TYPE.WARNINGS, data: warnings });
    } else {
      setFeedback(null);
    }
  };

  const convertToAWSFormat = (activeHour) => {
    const datetime = moment.tz(
      getDatetimeFormatted(activeHour),
      'MM/DD/YYYY HH:mm:ss',
      true,
      'America/Chicago',
    );
    return datetime.toISOString();
  };

  const getSchedulePlanogramUpdateInput = (csvText) => {
    const { rows } = getDataFromCsv(csvText);
    return rows.map((row) => {
      const requestType = selectedOption.value;
      let layoutName, physicalFridgeConfig, slot, vendItemName, activeHour;
      if (requestType === 'CREATE' || requestType === 'RECONFIG') {
        [layoutName, slot, vendItemName, physicalFridgeConfig] = row.split(',');
        activeHour = getCurrentTimeForActiveHour();
      } else {
        [layoutName, slot, vendItemName, activeHour] = row.split(',');
      }
      const input = {
        requestType,
        layoutName,
        slot,
        vendItemName,
        activeHour: convertToAWSFormat(activeHour),
      };
      if (requestType === 'CREATE' || requestType === 'RECONFIG') {
        input.physicalFridgeConfig = physicalFridgeConfig;
      }
      return input;
    });
  };

  const schedulePlanogramUpdate = async (schedulePlanogramUpdateInput) => {
    setErrors([]); // Clear previous results before starting

    // change batching for CREATE and RECONFIG to send one planogram at a time
    let batches = [];
    if (
      selectedOption.value === 'CREATE' ||
      selectedOption.value === 'RECONFIG'
    ) {
      batches = chunkNewPlanograms(schedulePlanogramUpdateInput);
    } else {
      batches = chunkArray(schedulePlanogramUpdateInput, 100);
    }

    const totalBatches = batches.length;
    let completedBatches = 0;
    setUploadProgress(0); // reset progress bar

    if (
      selectedOption.value === 'CREATE' ||
      selectedOption.value === 'RECONFIG'
    ) {
      // Per-planogram aggregation for CREATE / RECONFIG
      const successMessages = [];
      const failureMessages = [];

      const batchPromises = batches.map((batch, index) => {
        const layoutName = batch[0]?.layoutName || `Batch ${index + 1}`;
        return SchedulePlanogramUpdate(batch)
          .then((response) => {
            const {
              successCount = 0,
              totalCount = batch.length,
              errors: responseErrors = [],
            } = response || {};
            if (responseErrors.length > 0 || successCount !== totalCount) {
              const errorText = responseErrors.join('; ');
              failureMessages.push({
                description: `Planogram "${layoutName}" failed (${successCount}/${totalCount} records succeeded).${
                  errorText ? ` Errors: ${errorText}` : ''
                }`,
              });
            } else {
              successMessages.push({
                description: `Planogram "${layoutName}" processed successfully (${successCount} records).`,
              });
            }
          })
          .catch((error) => {
            failureMessages.push({
              description: `Planogram "${layoutName}" failed (0/${
                batch.length
              } records succeeded). Errors: ${
                error?.message || 'Unknown error'
              }`,
            });
          })
          .finally(() => {
            completedBatches += 1;
            setUploadProgress(
              Math.round((completedBatches / totalBatches) * 100),
            );
          });
      });

      await Promise.all(batchPromises);

      // Set separate boxes using existing components: errors for failures, feedback for successes
      if (successMessages.length > 0) {
        setFeedback({ type: VALIDATION_TYPE.SUCCESS, data: successMessages });
      } else {
        // Clear success feedback if none
        setFeedback(null);
      }
      setErrors(failureMessages);

      // Return true if at least one planogram succeeded
      return successMessages.length > 0;
    }

    // UPDATE: keep previous behavior (batch-level aggregation)
    let totalSuccessCount = 0;
    let allErrors = [];

    const batchPromises = batches.map((batch) =>
      SchedulePlanogramUpdate(batch)
        .then((response) => {
          const { successCount = 0, errors: responseErrors = [] } =
            response || {};
          totalSuccessCount += successCount;
          if (responseErrors.length) {
            allErrors = allErrors.concat(
              responseErrors.map((error) => ({ description: error })),
            );
          }
        })
        .catch((error) => {
          // Treat the whole batch as an error message
          allErrors.push({ description: error?.message || 'Unknown error' });
        })
        .finally(() => {
          completedBatches += 1;
          setUploadProgress(
            Math.round((completedBatches / totalBatches) * 100),
          );
        }),
    );

    await Promise.all(batchPromises);

    if (totalSuccessCount > 0) {
      setFeedback({
        type: VALIDATION_TYPE.SUCCESS,
        data: [
          {
            description: `Total of successfully validated and processed records: ${totalSuccessCount}.`,
          },
        ],
      });
    } else {
      setFeedback(null);
    }

    setErrors(allErrors);

    return totalSuccessCount > 0;
  };

  const submitHandler = (e) => {
    e.preventDefault();
    setFeedback(null);
    if (!csvFile || errors.length) return;
    const reader = new FileReader();
    reader.onload = async function (event) {
      setLoading(true);
      setUploadProgress(0);
      const content = event.target.result;
      const input = getSchedulePlanogramUpdateInput(content);
      await schedulePlanogramUpdate(input);
      setLoading(false);
    };
    reader.readAsText(csvFile);
  };

  const changeHandler = (option) => {
    setSelectedOption(option);
    setFeedback(null);
    setErrors([]);
    setUploadProgress(0);

    // If a file is already selected, re-validate it against the newly selected option
    if (csvFile) {
      const reader = new FileReader();
      reader.onload = function (event) {
        const content = event.target.result;
        validateCsvContent(content, option.value);
      };
      reader.readAsText(csvFile);
    }
  };

  const changeFileHandler = (e) => {
    // Clear previous messages immediately on new file selection
    setFeedback(null);
    setErrors([]);
    setUploadProgress(0);

    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function (event) {
        const content = event.target.result;
        validateCsvContent(content, selectedOption.value);
        setCsvFile(file);
      };
      reader.readAsText(file);
    }
  };

  return (
    <main className={styles.container}>
      <h1>Planograms</h1>
      <form className={styles.form} onSubmit={submitHandler}>
        <div className={styles.formWrapper}>
          <div>Select an option:</div>
          <Select
            options={PlanogramTaskOptions}
            onChange={changeHandler}
            value={selectedOption}
          />
        </div>
        {selectedOption.value !== PlanogramTaskOptions[0].value ? (
          <div className={styles.formWrapper}>
            <div>Upload CSV file:</div>
            <OverlayTrigger
              placement="right"
              overlay={
                selectedOption.value === 'UPDATE'
                  ? tooltipUpdate
                  : tooltipCreateReconfig
              }
            >
              <input type="file" accept=".csv" onChange={changeFileHandler} />
            </OverlayTrigger>
          </div>
        ) : null}
        <button
          className={styles.button}
          disabled={
            selectedOption.value === PlanogramTaskOptions[0].value ||
            errors.length ||
            !csvFile ||
            loading
          }
          type="submit"
        >
          {loading ? 'Loading...' : 'Submit'}
        </button>
      </form>
      {loading && (
        <div className={styles.progressBarContainer}>
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
        </div>
      )}
      {errors.length ? (
        <ValidationFeedback data={errors} type={VALIDATION_TYPE.ERRORS} />
      ) : null}
      {feedback ? (
        <ValidationFeedback data={feedback.data} type={feedback.type} />
      ) : null}
    </main>
  );
};

export default Planograms;
